import { useEffect, useState, type FC } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  SelectPicker,
  Toggle,
} from "rsuite";
import { Discount, DiscountType } from "../../../models/inventory/discount";
import CurrencyInput from "react-currency-input-field";

interface DiscountFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Discount) => void;
  discount?: Discount | null;
}

const DiscountForm: FC<DiscountFormProps> = ({
  open,
  onClose,
  onSubmit,
  discount,
}) => {
  const [data, setData] = useState<Discount>({
    type: DiscountType.PERCENTAGE,
    value: 0,
    start_date: new Date(),
    is_active: false,
  });

  useEffect(() => {
    if (discount) {
      setData({
        ...discount,
        start_date: new Date(discount.start_date),
        end_date: discount.end_date ? new Date(discount.end_date) : null,
      });
    }
  }, [discount]);
  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>
        <Modal.Title>Discount Form</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="w-full" layout="horizontal" fluid>
          <div className="form-group">
            <label>Discount Type</label>
            <div className="input">
              <SelectPicker
                block
                data={[
                  { label: "Percentage", value: DiscountType.PERCENTAGE },
                  { label: "Amount", value: DiscountType.AMOUNT },
                ]}
                value={data.type}
                onChange={(val) => {
                  setData({ ...data, type: val! });
                }}
                placeholder="Select a Discount"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Value</label>
            <div className="input">
              <CurrencyInput
                className="rs-input"
                value={data.value}
                groupSeparator="."
                decimalSeparator=","
                onValueChange={(value, name, values) => {
                  if (data.type === DiscountType.PERCENTAGE) {
                    setData({
                      ...data,
                      value: Math.min(values?.float ?? 0, 100),
                    });
                  } else {
                    setData({ ...data, value: values?.float ?? 0 });
                  }
                }}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Start Date</label>
            <div className="input">
              <DatePicker
                block
                value={data.start_date}
                onChange={(val) => {
                  setData({ ...data, start_date: val! });
                }}
                placeholder="Select a Start Date"
              />
            </div>
          </div>
          <div className="form-group">
            <label>End Date</label>
            <div className="input">
              <DatePicker
                block
                value={data.end_date}
                shouldDisableDate={(date) => date < data.start_date}
                onChange={(val) => {
                  setData({ ...data, end_date: val! });
                }}
                placeholder="Select an End Date"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Active</label>
            <div className="input">
              <Toggle
                checked={data.is_active}
                onChange={(val) => {
                  setData({ ...data, is_active: val });
                }}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <div className="input">
              <Input
                as="textarea"
                rows={3}
                value={data.notes}
                onChange={(value) => {
                  setData({ ...data, notes: value });
                }}
                placeholder="Enter any notes"
              />
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div className="pt-4">
          <Button
            appearance="primary"
            onClick={() => {
              onSubmit(data!);
              setData({
                type: DiscountType.PERCENTAGE,
                value: 0,
                start_date: new Date(),
                is_active: false,
              })
            }}
          >
            Submit
          </Button>
          <Button appearance="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default DiscountForm;
